import React, { useState, useEffect } from 'react'

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import ListPageHeader from '@shared/ListPageHeader'
import Can from '@shared/permissions/Can'
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler'
import {
  Button,
  Card,
  message,
  Space,
  Descriptions,
  Modal,
  Tag,
  Progress
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'

import { formatDateTime } from '../../../utils/dateTime'

export default function InventoryMonitorsShow({
  inventoryMonitor: initialData,
  inventoryMonitorService,
  role = 'admin',
  merchants = [],
  basePath = '/inventory_monitors'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState(false)
  const [data, setData] = useState(initialData)

  useEffect(() => {
    if (initialData) {
      setData(initialData)
    }
  }, [initialData])

  const handleDelete = () => {
    if (data?.deletable === false) {
      message.warning(t('common.error.permission_denied'))
      return
    }

    Modal.confirm({
      title: t('common.confirm.delete_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm.delete_description'),
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        setDeleting(true)
        try {
          const response = await inventoryMonitorService.delete(data.id)
          if (response.success) {
            message.success(t('common.message.delete_success'))
            navigate(basePath)
          } else {
            handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') })
          }
        } catch (error) {
          handleNetworkError(error, { t })
          console.error('Delete inventory monitor error:', error)
        } finally {
          setDeleting(false)
        }
      }
    })
  }

  const getMerchantName = (merchantId) => {
    if (!merchantId || merchants.length === 0) return '-'
    return merchants.find((merchant) => merchant.id === merchantId)?.name || '-'
  }

  const renderMechanism = (mechanism) => {
    const mechanismMap = {
      quantity: { text: t('shared.inventory_monitors.form.mechanisms.quantity'), color: 'blue' },
      turnover_days: { text: t('shared.inventory_monitors.form.mechanisms.turnover_days'), color: 'cyan' },
      stalled_days: { text: t('shared.inventory_monitors.form.mechanisms.stalled_days'), color: 'purple' },
    }
    const { text, color } = mechanismMap[mechanism] || { text: mechanism, color: 'default' }
    return <Tag color={color}>{text}</Tag>
  }

  const renderHealthStatus = () => {
    if (!data) return null

    const ratio = data.stock_health_ratio
    let status = 'success'
    let statusText = t('shared.inventory_monitors.list.health_status.healthy')

    if (ratio < 0.5) {
      status = 'exception'
      statusText = t('shared.inventory_monitors.list.health_status.critical')
    } else if (ratio < 1) {
      status = 'normal'
      statusText = t('shared.inventory_monitors.list.health_status.warning')
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Progress
          percent={Math.min(ratio * 100, 100)}
          status={status}
          format={(percent) => `${percent.toFixed(0)}%`}
        />
        <Tag color={status === 'success' ? 'green' : status === 'exception' ? 'red' : 'orange'}>
          {statusText}
        </Tag>
      </Space>
    )
  }

  if (!data) {
    return (
      <Card>
        <p>{t('common.error.record_not_found')}</p>
      </Card>
    )
  }

  const showEditButton = data?.editable !== false
  const showDeleteButton = data?.deletable !== false
  const headerBasePath = role === 'operator' ? '/operator' : '/admin'

  const headerActions = (
    <Space>
      {showEditButton && (
        <Can requiredPermission="inventories:write">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`${basePath}/${data.id}/edit`)}
          >
            {t('common.button.edit')}
          </Button>
        </Can>
      )}
      {showDeleteButton && (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          loading={deleting}
        >
          {t('common.button.delete')}
        </Button>
      )}
    </Space>
  )

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['stocks', 'stocks.trackers', { label: data.product?.sku || data.id }]}
        actions={headerActions}
      />
      <Card>
        <Descriptions column={1} bordered>
          {/* 基本資訊 */}
          {merchants.length > 0 && data.merchant_id && (
            <Descriptions.Item label={t('shared.inventory_monitors.list.columns.merchant_name')}>
              {getMerchantName(data.merchant_id)}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.product_info')}>
            <Link to={`/products/${data.product?.id}`}>
              {data.product?.name} ({data.product?.sku})
            </Link>
          </Descriptions.Item>

          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.inventory_type')}>
            {data.inventory_type?.name} ({data.inventory_type?.code})
          </Descriptions.Item>

          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.mechanism')}>
            {renderMechanism(data.mechanism)}
          </Descriptions.Item>

          {/* 追蹤設定 */}
          {data.mechanism === 'turnover_days' && data.lookback_days && (
            <Descriptions.Item label={t('shared.inventory_monitors.show.fields.lookback_days')}>
              {data.lookback_days} {t('common.unit.days')}
            </Descriptions.Item>
          )}

          {data.mechanism === 'turnover_days' && data.target_coverage_days && (
            <Descriptions.Item label={t('shared.inventory_monitors.show.fields.target_coverage_days')}>
              {data.target_coverage_days} {t('common.unit.days')}
            </Descriptions.Item>
          )}

          {data.mechanism === 'stalled_days' && data.lookback_days && (
            <Descriptions.Item label={t('shared.inventory_monitors.show.fields.lookback_days')}>
              {data.lookback_days} {t('common.unit.days')}
            </Descriptions.Item>
          )}

          {data.expiry_days !== null && data.expiry_days !== undefined && (
            <Descriptions.Item label={t('shared.inventory_monitors.show.fields.expiry_days')}>
              {data.expiry_days} {t('common.unit.days')}
            </Descriptions.Item>
          )}

          {/* 目前狀態 */}
          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.threshold')}>
            {data.threshold_stocks}
          </Descriptions.Item>

          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.current_stock')}>
            {data.tracking_stocks}
          </Descriptions.Item>

          <Descriptions.Item label={t('shared.inventory_monitors.show.fields.health_ratio')}>
            {renderHealthStatus()}
          </Descriptions.Item>

          {/* 時間戳記 */}
          <Descriptions.Item label={t('common.created_at')}>
            {data.created_at ? formatDateTime(data.created_at) : '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('common.updated_at')}>
            {data.updated_at ? formatDateTime(data.updated_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  )
}
